/**
 * Community section configs (CommunitySections.md).
 * Used by list/add/view/edit pages and sidebar.
 */

import {
  AlertCircle,
  HelpCircle,
  Gift,
  Search,
  MessageCircleQuestion,
} from "lucide-react";
import type { CommunitySectionConfig } from "./types";

export const IMMEDIATE_HELP: CommunitySectionConfig = {
  contentType: "URGENT_HELP", // Keep technical ID for now to avoid schema changes
  path: "immediate-help",
  label: "Time Sensitive Help",
  description: "Time-sensitive requests needing immediate attention",
  icon: AlertCircle,
  color: "text-red-600",
  expiryHours: 24,
};

export const URGENT_HELP = IMMEDIATE_HELP;

export const ASK_FOR_HELP: CommunitySectionConfig = {
  contentType: "ASK_FOR_HELP",
  path: "ask-for-help",
  label: "Ask for Help",
  description: "Everyday requests for neighbor-to-neighbor assistance",
  icon: HelpCircle,
  color: "text-blue-600",
};

export const SHARE_WITH_NEIGHBORS: CommunitySectionConfig = {
  contentType: "SHARE_WITH_NEIGHBORS",
  path: "share-with-neighbors",
  label: "Share with Neighbors",
  description: "Offer items, food, or resources freely to the community",
  icon: Gift,
  color: "text-green-600",
};

export const OTHER_REQUESTS: CommunitySectionConfig = {
  contentType: "OTHER_REQUESTS",
  path: "other-requests",
  label: "Other Requests",
  description: "Community-related requests that don't fit other sections",
  icon: MessageCircleQuestion,
  color: "text-amber-600",
};

/** All community section configs for iteration */
export const COMMUNITY_SECTIONS = [
  IMMEDIATE_HELP,
  ASK_FOR_HELP,
  SHARE_WITH_NEIGHBORS,
  OTHER_REQUESTS,
] as const;

export type CommunitySectionKey =
  (typeof COMMUNITY_SECTIONS)[number]["contentType"];

/**
 * Canonical Community breadcrumb dropdown items (same order and linking as Lost & Found).
 * Use this for all view pages (Time Sensitive Help, Ask, Share, Other Requests, Lost & Found) so
 * layout and linking are consistent.
 */
export function getCommunityBreadcrumbDropdownItems(
  locationCode: string | null | undefined,
): { label: string; href: string }[] {
  const base = (path: string) =>
    locationCode ? `/locations/${locationCode}/${path}` : `/${path}`;
  return [
    { label: "Time Sensitive Help", href: base("immediate-help") },
    { label: "Ask for Help", href: base("ask-for-help") },
    { label: "Share with Neighbors", href: base("share-with-neighbors") },
    { label: "Lost & Found", href: base("lost-found") },
    { label: "Other Requests", href: base("other-requests") },
    { label: "Buy & Sell", href: base("buy-sell") },
    { label: "Request & Offer", href: base("request-offer") },
    { label: "Food Court", href: base("food-court") },
    { label: "Retail", href: base("retail") },
    { label: "Services", href: base("services") },
  ];
}
